import React, { useState } from 'react';
import {
  Button, Typography, Box, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Stack, Snackbar, Alert, Slide,
} from '@mui/material';
import GetAppIcon from '@mui/icons-material/GetApp';
import CloseIcon from '@mui/icons-material/Close';
import IosShareIcon from '@mui/icons-material/IosShare';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import LaptopMacIcon from '@mui/icons-material/LaptopMac';
import AndroidIcon from '@mui/icons-material/Android';
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt';
import { usePWAInstall } from '../../hooks/usePWAInstall';

// Dismissed vetëm kur user klikon X – ruhet në localStorage
const DISMISSED_KEY = 'pwa-banner-dismissed-v1';

export default function PWAInstallBanner() {
  const { isInstallable, isInstalled, isIOS, isMacSafari, isAndroid, needsManual, install } =
    usePWAInstall();

  const [dismissed, setDismissed]   = useState(() => localStorage.getItem(DISMISSED_KEY) === '1');
  const [dialog, setDialog]         = useState(null); // 'ios' | 'mac' | null
  const [installing, setInstalling] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');

  // Mos shfaq nëse: jo installable, tashmë instaluar, ose user e mbylli me dorë
  if (!isInstallable || isInstalled || dismissed) return null;

  /* ── Handlers ── */
  const handleInstall = async () => {
    if (isIOS)       { setDialog('ios'); return; }
    if (isMacSafari) { setDialog('mac'); return; }

    setInstalling(true);
    const result = await install();
    setInstalling(false);

    if (result === 'accepted') {
      setSuccessMsg('Nexuma u instalua me sukses!');
      // NUKU dismiss – 'appinstalled' event do ta fshehë banerin kur instalimi përfundon
    }
  };

  // Vetëm kur user klikon X mbyllet banerit përgjithmonë
  const handleUserDismiss = () => {
    localStorage.setItem(DISMISSED_KEY, '1');
    setDismissed(true);
  };

  /* ── Platform label ── */
  const platformIcon = isIOS ? <IosShareIcon sx={{ fontSize: 18 }} />
    : isMacSafari ? <LaptopMacIcon sx={{ fontSize: 18 }} />
    : isAndroid   ? <AndroidIcon sx={{ fontSize: 18 }} />
    : <SystemUpdateAltIcon sx={{ fontSize: 18 }} />;

  const platformLabel = isIOS ? 'Shto në Home Screen'
    : isMacSafari ? 'Shto në Dock (Mac)'
    : isAndroid   ? 'Instalo Nexuma'
    : 'Instalo Nexuma';

  const platformSub = isIOS
    ? 'Hap aplikacionin direkt nga ekrani fillestar, pa hapur browserin.'
    : isMacSafari
    ? 'Shtoje në Dock dhe hape si aplikacion vetjak në Mac.'
    : 'Hap aplikacionin direkt nga ekrani fillestar, pa hapur browserin.';

  return (
    <>
      {/* ══ Banner kryesor ══ */}
      <Slide direction="up" in mountOnEnter unmountOnExit>
        <Box
          sx={{
            position: 'fixed',
            bottom: {
              xs: 'calc(60px + env(safe-area-inset-bottom, 0px) + 10px)',
              sm: 16,
            },
            left: { xs: 8, sm: 'auto' },
            right: { xs: 8, sm: 16 },
            zIndex: 1500,
            width: { xs: 'calc(100vw - 16px)', sm: 'auto' },
            maxWidth: { xs: '100%', sm: 420 },
          }}
        >
          <Box
            onClick={handleInstall}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleInstall();
              }
            }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              px: 2,
              py: 1.75,
              borderRadius: 3,
              bgcolor: '#ffffff',
              boxShadow: '0 10px 30px rgba(15,23,42,0.10), 0 2px 6px rgba(15,23,42,0.06)',
              border: '1px solid rgba(15,23,42,0.06)',
              cursor: installing ? 'progress' : 'pointer',
              transition: 'transform 120ms ease, box-shadow 120ms ease',
              '&:hover': {
                transform: 'translateY(-1px)',
                boxShadow: '0 14px 36px rgba(15,23,42,0.14), 0 2px 6px rgba(15,23,42,0.06)',
              },
            }}
          >
            {/* gradient icon */}
            <Box
              sx={{
                width: 52, height: 52, borderRadius: '50%',
                background: 'linear-gradient(135deg, #5BA9BD 0%, #1C2536 100%)',
                color: '#ffffff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
                boxShadow: '0 6px 14px rgba(28,37,54,0.25)',
              }}
            >
              {React.cloneElement(platformIcon, { sx: { fontSize: 26 } })}
            </Box>

            {/* text */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                sx={{
                  fontWeight: 800,
                  color: '#0F172A',
                  fontSize: '1rem',
                  lineHeight: 1.25,
                  mb: 0.25,
                }}
              >
                {installing ? 'Duke instaluar…' : platformLabel}
              </Typography>
              <Typography
                sx={{
                  color: 'rgba(15,23,42,0.55)',
                  fontSize: '0.82rem',
                  lineHeight: 1.35,
                }}
              >
                {platformSub}
              </Typography>
            </Box>

            {/* close btn */}
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); handleUserDismiss(); }}
              sx={{
                color: 'rgba(15,23,42,0.4)',
                p: 0.5,
                flexShrink: 0,
                alignSelf: 'flex-start',
                '&:hover': { color: 'rgba(15,23,42,0.7)', bgcolor: 'rgba(15,23,42,0.05)' },
              }}
              aria-label="Mbyll"
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        </Box>
      </Slide>

      {/* ══ Dialog: iOS Safari ══ */}
      <ManualDialog
        open={dialog === 'ios'}
        onClose={() => setDialog(null)}
        title="Instalo në iPhone / iPad"
        subtitle="Për të instaluar Nexumi si app në iOS, ndiq këto hapa:"
        steps={[
          {
            icon: <IosShareIcon sx={{ color: '#007AFF', fontSize: 20 }} />,
            text: 'Kliko butonin "Share" (katror me shigjetë lart) në fund të Safari',
          },
          {
            icon: <GetAppIcon sx={{ color: '#007AFF', fontSize: 20 }} />,
            text: 'Zgjidh "Add to Home Screen"',
          },
          {
            icon: <PhoneIphoneIcon sx={{ color: '#007AFF', fontSize: 20 }} />,
            text: 'Kliko "Add" — ikonja shfaqet në Home Screen',
          },
        ]}
        note="Funksionon vetëm me Safari. Nëse je duke përdorur Chrome, hap faqen me Safari."
      />

      {/* ══ Dialog: macOS Safari ══ */}
      <ManualDialog
        open={dialog === 'mac'}
        onClose={() => setDialog(null)}
        title="Instalo në Mac (Safari)"
        subtitle="Për të instaluar Nexumi si app në macOS, ndiq këto hapa:"
        steps={[
          {
            icon: <IosShareIcon sx={{ color: '#007AFF', fontSize: 20 }} />,
            text: 'Kliko ikonën "Share" në toolbar të Safari (katror me shigjetë lart)',
          },
          {
            icon: <LaptopMacIcon sx={{ color: '#007AFF', fontSize: 20 }} />,
            text: 'Zgjidh "Add to Dock..." nga menuja',
          },
          {
            icon: <GetAppIcon sx={{ color: '#007AFF', fontSize: 20 }} />,
            text: 'Kliko "Add" — app shfaqet në Dock dhe Launchpad',
          },
        ]}
        note="Kjo veçori kërkon macOS Sonoma (14+) dhe Safari 17+. Në Chrome/Edge instalimi bëhet automatikisht."
      />

      {/* ══ Success snackbar ══ */}
      <Snackbar
        open={Boolean(successMsg)}
        autoHideDuration={4000}
        onClose={() => setSuccessMsg('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setSuccessMsg('')} sx={{ fontWeight: 600 }}>
          {successMsg}
        </Alert>
      </Snackbar>
    </>
  );
}

/* ── Komponent i përbashkët për dialog me udhëzime manuale ── */
function ManualDialog({ open, onClose, title, subtitle, steps, note }) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, m: { xs: 1.5, sm: 3 } } }}
    >
      <DialogTitle sx={{ fontWeight: 700, pb: 0.5, fontSize: '1rem' }}>
        {title}
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
            {subtitle}
          </Typography>

          {steps.map((item, i) => (
            <Stack key={i} direction="row" spacing={1.5} alignItems="flex-start">
              <Box
                sx={{
                  width: 24, height: 24, borderRadius: '50%',
                  bgcolor: 'primary.light', color: 'primary.main',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontWeight: 800, fontSize: '0.72rem', flexShrink: 0, mt: 0.2,
                }}
              >
                {i + 1}
              </Box>
              <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ flex: 1 }}>
                <Box sx={{ flexShrink: 0, mt: 0.1 }}>{item.icon}</Box>
                <Typography variant="body2" sx={{ fontSize: '0.8rem', lineHeight: 1.4 }}>
                  {item.text}
                </Typography>
              </Stack>
            </Stack>
          ))}

          {note && (
            <Alert severity="info" sx={{ fontSize: '0.74rem', py: 0.5 }}>
              {note}
            </Alert>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 2, pb: 1.5 }}>
        {/* "Kuptova" mbyll vetëm dialogun – banneri mbetet i dukshëm */}
        <Button onClick={onClose} size="small" sx={{ fontWeight: 600 }}>
          Kuptova
        </Button>
      </DialogActions>
    </Dialog>
  );
}
